import React from 'react';
import { Card, Typography, Tag, App, Alert, Space, Skeleton } from 'antd';
import { CreditCardOutlined, BankOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useGetBoolSettingQuery, useGetSettingQuery } from '../services/settingsApi';
import { useIsModuleEnabled } from '../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../constants/moduleKeys';

// Mismo canal de soporte que login/registro y la landing
const SUPPORT_WHATSAPP_URL = 'https://wa.me/522206294774';

const { Title, Text } = Typography;

interface ProviderOption {
  key: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  route: string;
  color: string;
  active: boolean;
  /** Visible pero bloqueada: al dar clic explica que viene pronto (lead a soporte). */
  comingSoon: boolean;
}

/**
 * Configuración > Terminales de cobro. Hub de proveedores de terminal
 * bancaria: el restaurante elige UNO (la exclusividad la aplica el backend al
 * activar) y lo configura en su propia página. El "próximamente" de Mercado
 * Pago (módulo en piloto, solo soporte lo enciende) vive aquí, no en el menú
 * general de Configuración.
 */
const TerminalesSettings: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { modal } = App.useApp();

  const { enabled: isMpPointEnabled, isLoading: mpModuleLoading } =
    useIsModuleEnabled(MODULE_KEYS.MercadoPagoPoint, { allowPlanBypass: false });
  const { data: mpEnabledSetting } = useGetBoolSettingQuery('MercadoPagoPointEnabled');
  const { data: getnetEnabledSetting } = useGetBoolSettingQuery('GetnetPointEnabled');
  const { data: providerSetting, isLoading: providerLoading } = useGetSettingQuery('TerminalProvider');

  const activeProvider = providerSetting?.value ?? '';

  const options: ProviderOption[] = [
    {
      key: 'mercadopago',
      icon: <CreditCardOutlined style={{ fontSize: isMobile ? 32 : 40 }} />,
      label: 'Mercado Pago Point',
      description: 'Envía el cobro a tu terminal Point; el comensal paga y deja propina ahí mismo.',
      route: '/mercadopago-settings',
      color: '#009ee3',
      active: activeProvider === 'mercadopago' && (mpEnabledSetting?.value ?? false),
      comingSoon: !isMpPointEnabled,
    },
    {
      key: 'getnet',
      icon: <BankOutlined style={{ fontSize: isMobile ? 32 : 40 }} />,
      label: 'Santander Getnet',
      description: 'Envía el cobro a tu terminal G-Smart de Getnet dando de alta su número de serie.',
      route: '/getnet-settings',
      color: '#ec0000',
      active: activeProvider === 'getnet' && (getnetEnabledSetting?.value ?? false),
      comingSoon: false,
    },
  ];

  const handleClick = (option: ProviderOption) => {
    if (option.comingSoon) {
      modal.info({
        title: `${option.label} — Próximamente`,
        content: (
          <div>
            <p>
              Estamos terminando de validar esta integración: podrás enviar el cobro
              directo a tu terminal Point y la propina se captura ahí mismo.
            </p>
            <p style={{ marginBottom: 0 }}>
              ¿Te interesa usarla en tu restaurante? Contáctanos y te avisamos en
              cuanto esté lista — incluso puedes participar en las primeras pruebas.
            </p>
          </div>
        ),
        okText: 'Contactar a soporte',
        okButtonProps: { style: { backgroundColor: '#25D366', borderColor: '#25D366' } },
        onOk: () => window.open(SUPPORT_WHATSAPP_URL, '_blank', 'noopener'),
        maskClosable: true,
      });
      return;
    }
    navigate(option.route);
  };

  if (mpModuleLoading || providerLoading) {
    return (
      <div style={{ padding: isMobile ? 16 : 24 }}>
        <Skeleton active paragraph={{ rows: 5 }} />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <CreditCardOutlined style={{ color: 'var(--color-primary)' }} />
          Terminales de cobro
        </Title>
        <Text type="secondary">
          Cobra las cuentas enviando el pago directo a tu terminal bancaria
        </Text>
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 720 }}>
        <Alert
          type="info"
          showIcon
          message="Solo puede haber un proveedor de terminal activo a la vez. Para cambiar de proveedor, desactiva el actual y activa el otro."
        />

        {options.map((option) => (
          <Card
            key={option.key}
            hoverable
            onClick={() => handleClick(option)}
            style={{ borderRadius: isMobile ? 8 : 12 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div
                style={{
                  color: option.color,
                  padding: 12,
                  borderRadius: '50%',
                  backgroundColor: `${option.color}15`,
                  display: 'flex',
                }}
              >
                {option.icon}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Text strong style={{ fontSize: 16 }}>{option.label}</Text>
                  {option.active && <Tag color="green">Activa</Tag>}
                  {option.comingSoon && <Tag color="blue">Próximamente</Tag>}
                </div>
                <Text type="secondary" style={{ fontSize: 13 }}>{option.description}</Text>
              </div>
            </div>
          </Card>
        ))}
      </Space>
    </div>
  );
};

export default TerminalesSettings;
